/**
 * Google Sheets Backup Dashboard Component
 * Main dashboard for Google Sheets backup and integration
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNotifications } from '../common/NotificationSystem';
import { googleSheetsService } from '../../services/googleSheetsService';
import { useRBAC } from '../../hooks/useRBAC';
import ProfessionalLoader from "../common/ProfessionalLoader";
import GoogleSheetsSyncLogs from './GoogleSheetsSyncLogs';
import GoogleSheetsMemberSync from './GoogleSheetsMemberSync';
import GoogleSheetsImport from './GoogleSheetsImport';
import './GoogleSheetsBackupDashboard.css';

const GoogleSheetsBackupDashboard = () => {
  const { showSuccess, showError } = useNotifications();
  const { canManageIntegrations } = useRBAC();

  // State management
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [connectionStatus, setConnectionStatus] = useState(null);

  // Load dashboard data
  const loadDashboardData = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await googleSheetsService.getBackupDashboard();

      if (response.success) {
        const data = response.data || {};
        // Ensure all required fields have default values
        const dashboardData = {
          sync_status: data.sync_status || {
            is_configured: false,
            auto_sync_enabled: false,
            sync_due: false,
            consecutive_failures: 0
          },
          member_statistics: data.member_statistics || {
            total_members: 0,
            synced_members: 0,
            failed_sync_members: 0,
            never_synced_members: 0
          },
          sync_health: data.sync_health || {
            syncs_last_24h: 0,
            successful_syncs_last_24h: 0,
            failed_syncs_last_24h: 0,
            avg_sync_duration: 0
          },
          recent_syncs: data.recent_syncs || [],
          settings: data.settings || {}
        };
        setDashboardData(dashboardData);
        
        // Update connection status from dashboard data
        if (data.sync_status && data.sync_status.is_configured) {
          setConnectionStatus('connected');
        } else {
          setConnectionStatus('disconnected');
        }
      } else {
        showError(response.message || 'Failed to load dashboard data', 'Error');
        setConnectionStatus('error');
      }
    } catch (error) {
      showError('Failed to load dashboard data', 'Error');
      setConnectionStatus('error');
    } finally {
      setIsLoading(false);
    }
  }, [showError]);

  // Load connection status
  const loadConnectionStatus = useCallback(async () => {
    try {
      const response = await googleSheetsService.getConnectionStatus();
      if (response.success) {
        setConnectionStatus('connected');
      } else {
        setConnectionStatus('disconnected');
      }
    } catch (error) {
      // If connection status fails, check dashboard data instead
      setConnectionStatus('disconnected');
    }
  }, []);

  // Load dashboard data on component mount
  useEffect(() => {
    loadDashboardData();
    loadConnectionStatus();
  }, [loadDashboardData, loadConnectionStatus]);

  // Handle trigger sync
  const handleTriggerSync = async (syncType = 'manual', notes = '') => {
    setIsProcessing(true);
    try {
      const response = await googleSheetsService.triggerBackupSync();

      if (response.success) {
        showSuccess('Backup sync triggered successfully', 'Success');
        loadDashboardData();
      } else {
        showError(response.message || 'Failed to trigger sync', 'Error');
      }
    } catch (error) {
      showError('Failed to trigger sync', 'Error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle test connection
  const handleTestConnection = async () => {
    setIsProcessing(true);
    try {
      const response = await googleSheetsService.testBackupConnection();

      if (response.success) {
        showSuccess('Google Sheets connection successful', 'Success');
        setConnectionStatus('connected');
      } else {
        showError(response.message || 'Connection test failed', 'Error');
        setConnectionStatus('disconnected');
      }
    } catch (error) {
      showError('Connection test failed', 'Error');
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="google-sheets-backup-dashboard">
        <div className="loading-container">
          <ProfessionalLoader size="large" variant="default" />
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="google-sheets-backup-dashboard">
        <div className="error-state">
          <h3>Failed to Load Dashboard</h3>
          <p>Unable to load Google Sheets backup dashboard data.</p>
          <button className="btn btn-primary" onClick={loadDashboardData}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const {
    sync_status = {},
    member_statistics = {},
    sync_health = {},
    recent_syncs = [],
  } = dashboardData;

  return (
    <div className="google-sheets-backup-dashboard">
      {/* Header */}
      <div className="google-sheets-backup-dashboard-header">
        <div className="header-content">
          <h2>📊 Google Sheets Backup Dashboard</h2>
          <p>Monitor and manage Google Sheets backup synchronization</p>
        </div>
        <div className="header-actions">
          <div className="connection-status">
            <span className={`status-indicator ${connectionStatus}`}>
              {connectionStatus === 'connected' ? '🟢' :
                connectionStatus === 'disconnected' ? '🔴' :
                  connectionStatus === 'error' ? '⚠️' : '⚪'}
            </span>
            <span className="status-text">
              {connectionStatus === 'connected' ? 'Connected' :
                connectionStatus === 'disconnected' ? 'Disconnected' :
                  connectionStatus === 'error' ? 'Error' : 'Unknown'}
            </span>
          </div>
          <button
            className="btn btn-outline"
            onClick={handleTestConnection}
            disabled={isProcessing || !canManageIntegrations}
          >
            {isProcessing ? 'Testing...' : 'Test Connection'}
          </button>
          <button
            className="btn btn-primary"
            onClick={() => handleTriggerSync('manual', 'Manual sync triggered from dashboard')}
            disabled={isProcessing || !canManageIntegrations}
          >
            {isProcessing ? 'Syncing...' : 'Sync All Members'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="dashboard-tabs">
        <button
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 Overview
        </button>
        <button
          className={`tab-button ${activeTab === 'logs' ? 'active' : ''}`}
          onClick={() => setActiveTab('logs')}
        >
          📋 Sync Logs
        </button>
        <button
          className={`tab-button ${activeTab === 'sync' ? 'active' : ''}`}
          onClick={() => setActiveTab('sync')}
        >
          🔄 Member Sync
        </button>
        <button
          className={`tab-button ${activeTab === 'import' ? 'active' : ''}`}
          onClick={() => setActiveTab('import')}
        >
          📥 Import Data
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'overview' && (
          <div className="overview-tab">
            {/* Sync Status */}
            <div className="status-section">
              <h3>Sync Status</h3>
              <div className="status-cards">
                <div className={`status-card ${sync_status.is_configured ? 'configured' : 'not-configured'}`}>
                  <div className="status-icon">
                    {sync_status.is_configured ? '✅' : '❌'}
                  </div>
                  <div className="status-content">
                    <h4>Configuration</h4>
                    <p>{sync_status.is_configured ? 'Configured' : 'Not Configured'}</p>
                  </div>
                </div>

                <div className={`status-card ${sync_status.auto_sync_enabled ? 'enabled' : 'disabled'}`}>
                  <div className="status-icon">
                    {sync_status.auto_sync_enabled ? '🔄' : '⏸️'}
                  </div>
                  <div className="status-content">
                    <h4>Auto Sync</h4>
                    <p>{sync_status.auto_sync_enabled ? 'Enabled' : 'Disabled'}</p>
                  </div>
                </div>

                <div className={`status-card ${sync_status.sync_due ? 'due' : 'up-to-date'}`}>
                  <div className="status-icon">
                    {sync_status.sync_due ? '⏰' : '✅'}
                  </div>
                  <div className="status-content">
                    <h4>Sync Status</h4>
                    <p>{sync_status.sync_due ? 'Sync Due' : 'Up to Date'}</p>
                  </div>
                </div>

                <div className={`status-card ${(sync_status.consecutive_failures || 0) > 0 ? 'warning' : 'healthy'}`}>
                  <div className="status-icon">
                    {(sync_status.consecutive_failures || 0) > 0 ? '⚠️' : '✅'}
                  </div>
                  <div className="status-content">
                    <h4>Health</h4>
                    <p>{(sync_status.consecutive_failures || 0) > 0 ? `${sync_status.consecutive_failures || 0} Failures` : 'Healthy'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Member Statistics */}
            <div className="statistics-section">
              <h3>Member Statistics</h3>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon">👥</div>
                  <div className="stat-content">
                    <h4>Total Members</h4>
                    <p className="stat-value">{member_statistics.total_members || 0}</p>
                  </div>
                </div>

                <div className="stat-card synced">
                  <div className="stat-icon">✅</div>
                  <div className="stat-content">
                    <h4>Synced</h4>
                    <p className="stat-value">{member_statistics.synced_members || 0}</p>
                  </div>
                </div>

                <div className="stat-card failed">
                  <div className="stat-icon">❌</div>
                  <div className="stat-content">
                    <h4>Failed</h4>
                    <p className="stat-value">{member_statistics.failed_sync_members || 0}</p>
                  </div>
                </div>

                <div className="stat-card pending">
                  <div className="stat-icon">⏳</div>
                  <div className="stat-content">
                    <h4>Never Synced</h4>
                    <p className="stat-value">{member_statistics.never_synced_members || 0}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Sync Health */}
            <div className="health-section">
              <h3>Sync Health (Last 24 Hours)</h3>
              <div className="health-cards">
                <div className="health-card">
                  <div className="health-icon">📊</div>
                  <div className="health-content">
                    <h4>Total Syncs</h4>
                    <p className="health-value">{sync_health.syncs_last_24h || 0}</p>
                  </div>
                </div>

                <div className="health-card">
                  <div className="health-icon">✅</div>
                  <div className="health-content">
                    <h4>Successful</h4>
                    <p className="health-value">{sync_health.successful_syncs_last_24h || 0}</p>
                  </div>
                </div>

                <div className="health-card">
                  <div className="health-icon">❌</div>
                  <div className="health-content">
                    <h4>Failed</h4>
                    <p className="health-value">{sync_health.failed_syncs_last_24h || 0}</p>
                  </div>
                </div>

                <div className="health-card">
                  <div className="health-icon">⏱️</div>
                  <div className="health-content">
                    <h4>Avg Duration</h4>
                    <p className="health-value">{Math.round(sync_health.avg_sync_duration || 0)}s</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Syncs */}
            <div className="recent-syncs-section">
              <h3>Recent Syncs</h3>
              <div className="recent-syncs-list">
                {recent_syncs.length > 0 ? (
                  recent_syncs.map(sync => (
                    <div key={sync.id} className="recent-sync-item">
                      <div className="sync-info">
                        <span className="sync-type">{sync.sync_type}</span>
                        <span className={`sync-status ${sync.status}`}>{sync.status}</span>
                      </div>
                      <div className="sync-details">
                        <span className="sync-time">
                          {new Date(sync.started_at).toLocaleString()}
                        </span>
                        <span className="sync-members">
                          {sync.synced_members || 0} synced, {sync.failed_members || 0} failed
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-recent-syncs">
                    <p>No recent syncs found</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'logs' && (
          <GoogleSheetsSyncLogs />
        )}


        {activeTab === 'sync' && (
          <GoogleSheetsMemberSync />
        )}
        {activeTab === 'import' && (
          <GoogleSheetsImport />
        )}
      </div>
    </div>
  );
};

export default GoogleSheetsBackupDashboard;
